import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiClock, FiMail, FiMessageCircle } = FiIcons;

const Contact = () => {
  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone Number',
      details: ['+971 50 165 6278'],
      action: { text: 'Call Now', link: 'tel:+971501656278' }
    },
    {
      icon: FiMapPin,
      title: 'Address',
      details: [
        'Street 3 - Al Barsha South Third',
        'Al Barsha South - Dubai',
        'United Arab Emirates'
      ],
      action: { text: 'Get Directions', link: 'https://maps.app.goo.gl/VdmfWPBpoRV6vnha8' }
    },
    {
      icon: FiClock,
      title: 'Opening Hours',
      details: [
        'Monday - Sunday',
        '9:00 AM - 11:00 PM',
        'No Appointment Needed'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-[#826046] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Get in touch with us to book your appointment or for any inquiries about our services
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="bg-[#826046] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={info.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2 mb-6">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600">{detail}</p>
                  ))}
                </div>
                {info.action && (
                  <a
                    href={info.action.link}
                    target={info.action.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.action.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="bg-[#826046] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#6d4d37] transition-colors duration-300 inline-block"
                  >
                    {info.action.text}
                  </a>
                )}
              </motion.div>
            ))}
          </div>

          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-lg overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Find Us on Map</h2>
              <p className="text-gray-600">Located in the heart of Al Barsha South, Dubai</p>
            </div>
            <div className="w-full h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3614.3413722467926!2d55.229020500000004!3d25.0564163!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6f51ac5c0969%3A0x2ce6b6d512e9dfd7!2sMr.%20Shaver%20Gents%20Salon!5e0!3m2!1sen!2s!4v1764252875680!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mr. Shaver Gents Salon Location"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ready to Book Your Appointment?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              No appointment necessary! Walk-ins are welcome, or call us to reserve your preferred time slot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+971501656278"
                className="bg-[#826046] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#6d4d37] transition-colors duration-300 flex items-center"
              >
                <SafeIcon icon={FiPhone} className="mr-2 w-5 h-5" />
                Call: +971 50 165 6278
              </a>
              <a
                href="https://maps.app.goo.gl/VdmfWPBpoRV6vnha8"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-[#826046] text-[#826046] px-8 py-4 rounded-lg font-semibold hover:bg-[#826046] hover:text-white transition-colors duration-300 flex items-center"
              >
                <SafeIcon icon={FiMapPin} className="mr-2 w-5 h-5" />
                Visit Us Today
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Quick answers to common questions about our services
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: 'Do I need an appointment?',
                answer: 'No appointment is necessary! We welcome walk-ins throughout our operating hours. However, you can call us to check availability or reserve a preferred time slot.'
              },
              {
                question: 'What are your operating hours?',
                answer: 'We are open 7 days a week from 9:00 AM to 11:00 PM, making it convenient for you to visit us at your preferred time.'
              },
              {
                question: 'What services do you offer?',
                answer: 'We offer a complete range of men\'s grooming services including haircuts, beard trimming, traditional shaving, hot towel treatments, hair washing, and face treatments.'
              },
              {
                question: 'Do you accept card payments?',
                answer: 'Yes, we accept both cash and card payments for your convenience. We strive to make your visit as comfortable as possible.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;